import React, { useState, useMemo } from 'react';
import { IconButton, SimpleGrid, Box, HStack, Divider } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
} from 'date-fns';
import { Modal } from 'Molecules/Modal';
import { Typography } from 'Tokens';

export const DeadlinePicker = ({
  value,
  onChange,
  onClose,
  isOpen,
}: {
  value?: Date;
  onChange?: (d: Date) => void;
  onClose: () => void;
  isOpen: boolean;
}) => {
  const [currentMonth, setCurrentMonth] = useState(value ?? new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(value ?? null);
  const weeks = useMemo(() => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentMonth]);
  const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const handlePrev = () => setCurrentMonth(subMonths(currentMonth, 1));
  const handleNext = () => setCurrentMonth(addMonths(currentMonth, 1));
  const pick = (day: Date) => {
    if (!isSameMonth(day, currentMonth)) return;
    setSelectedDate(day);
    onChange?.(day);
    onClose();
  };
  return (
    <>
      <Modal
        title="Set deadline"
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        size="lg"
        hasFooter={false}
      >
        <HStack align="center" justify="space-between" mb="12px">
          <IconButton
            aria-label="Previous month"
            icon={<ChevronLeftIcon />}
            variant="ghost"
            size="md"
            onClick={handlePrev}
          />
          <Typography fontWeight="medium">{format(currentMonth, 'LLLL yyyy')}</Typography>
          <IconButton
            aria-label="Next month"
            icon={<ChevronRightIcon />}
            variant="ghost"
            size="md"
            onClick={handleNext}
          />
        </HStack>
        <Divider mb="12px" borderColor="border.decorative" />
        <SimpleGrid columns={7} textAlign="center" mb={2}>
          {WEEKDAYS.map((wd) => (
            <Typography key={wd} fontSize="sm" fontWeight="medium" color="gray.600">
              {wd}
            </Typography>
          ))}
        </SimpleGrid>
        <SimpleGrid columns={7} textAlign="center" rowGap={3} columnGap={2}>
          {weeks.map((day) => {
            const inMonth = isSameMonth(day, currentMonth);
            const isSelected = selectedDate && isSameDay(day, selectedDate);
            return (
              <Box
                key={day.toISOString()}
                onClick={() => pick(day)}
                cursor={inMonth ? 'pointer' : 'default'}
                opacity={inMonth ? 1 : 0.3}
                borderRadius="full"
                w={8}
                h={8}
                lineHeight="32px"
                margin="auto"
                textAlign="center"
                _hover={inMonth ? { bg: 'gray.100' } : {}}
                bg={isSelected ? 'text.selected' : 'transparent'}
                color={isSelected ? 'white' : 'text.muted'}
              >
                {format(day, 'd')}
              </Box>
            );
          })}
        </SimpleGrid>
      </Modal>
    </>
  );
};
